// src/features/auth/OTPVerification.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context";
import { Button, Alert } from "react-bootstrap";
import logo from "../../img/logo.png";
import golfBg from "../../img/golf-bg.jpg";
import {
  Mail,
  Shield,
  CheckCircle,
  Clock,
  RefreshCw,
  Lock,
  ArrowLeft,
  Fingerprint,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import "./OTPVerification.css"; // We'll create this CSS file

export default function OTPVerification() {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [canResend, setCanResend] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();

  const location = useLocation();
  const { email, session_id } = location.state || {};

  // If no email or session_id, redirect back to login
  useEffect(() => {
    if (!email || !session_id) {
      navigate("/");
    }
  }, [email, session_id, navigate]);

  const { verifyRetailOneOTP, resendRetailOneOTP } = useAuth();

  const inputRefs = useRef([]);

  useEffect(() => {
    // Auto-focus first input on mount with animation delay
    const timer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0 && !success) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [timeLeft, success]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (index, value) => {
    // Only accept numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      const pastedData = e.clipboardData?.getData("text") || "";
      const numbers = pastedData.replace(/\D/g, "").slice(0, 6);
      if (numbers) {
        const newOtp = [...otp];
        for (let i = 0; i < numbers.length && i < 6; i++) {
          newOtp[i] = numbers[i];
        }
        setOtp(newOtp);
        const lastIndex = Math.min(numbers.length - 1, 5);
        inputRefs.current[lastIndex]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData?.getData("text") || "";
    const numbers = pastedData.replace(/\D/g, "").slice(0, 6);
    if (numbers) {
      const newOtp = [...otp];
      for (let i = 0; i < numbers.length && i < 6; i++) {
        newOtp[i] = numbers[i];
      }
      setOtp(newOtp);
      const lastIndex = Math.min(numbers.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log('🔵 handleSubmit called');
    // //console.log('🔵 verifyGolfOTP function:', typeof verifyGolfOTP);
    // //console.log('🔵 verifyGolfOTP:', verifyGolfOTP);

    const otpValue = otp.join("");
    // console.log('🔵 OTP Value:', otpValue);
    // console.log('🔵 Email:', email);
    // console.log('🔵 Session ID:', session_id);

    if (otpValue.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    setError("");

    try {
      //console.log('🔵 Calling verifyGolfOTP...');
      const result = await verifyRetailOneOTP(email, otpValue, session_id);
      //console.log('🔵 Result from verifyGolfOTP:', result);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/retailone-dashboard");
        }, 1500);
      } else {
        // console.log("Full result object:", result);
        // console.log("result.error:", result.error);
        // console.log("result.status:", result.status);

        if (result.status === 429) {
          setError("Too many failed attempts. Redirecting to login...");
          setTimeout(() => navigate("/"), 3000);
        } else if (
          result.error?.includes("session") ||
          result.error?.includes("Session")
        ) {
          setError("Your session has expired. Please log in again.");
          setTimeout(() => navigate("/"), 2000);
        } else if (
          result.error?.includes("attempt") ||
          result.error?.includes("attempts")
        ) {
          setError(result.error);
        } else if (
          result.error?.includes("blocked") ||
          result.error?.includes("locked") ||
          result.error?.includes("minutes")
        ) {
          setError("Too many failed attempts. Redirecting to login...");
          setTimeout(() => navigate("/"), 3000);
        } else if (result.error?.includes("expired")) {
          setError(
            "Your verification code has expired. Please request a new one.",
          );
        } else {
          setError("Invalid verification code. Please try again.");
        }

        const container = document.querySelector(".otp-container");
        if (container) {
          container.style.animation = "shake 0.5s ease-in-out";
          setTimeout(() => {
            container.style.animation = "";
          }, 500);
        }
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      // console.log("🔵 Caught error:", err);
      if (
        err.status === 429 ||
        err.message?.includes("locked") ||
        err.message?.includes("blocked") ||
        err.message?.includes("minutes")
      ) {
        setError("Too many failed attempts. Redirecting to login...");
        setTimeout(() => navigate("/"), 3000);
      } else if (
        err.message?.includes("wait") ||
        err.message?.includes("seconds")
      ) {
        setError(err.message);
      } else {
        setError("Failed to verify code. Please try again.");
      }
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    setResendSuccess(false);
    try {
      await resendRetailOneOTP(email, session_id);
      setTimeLeft(120);
      setCanResend(false);
      setError("");
      setResendSuccess(true);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      // Success feedback
      setSuccess(false);
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (err) {
      setError("Failed to resend code. Please try again.");
    }
    setLoading(false);
  };

  const handleBack = () => {
    navigate("/");
  };

  // Add shake animation
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
        20%, 40%, 60%, 80% { transform: translateX(8px); }
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes shimmer {
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
      }
      .otp-input {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .otp-input:focus {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(14, 165, 233, 0.15);
      }
      .otp-input-filled {
        animation: pulse 0.3s ease-in-out;
      }
      .success-check {
        animation: fadeInUp 0.6s ease-out;
      }
      .shimmer-text {
        background: linear-gradient(90deg, #0ea5e9, #0284c7, #0ea5e9);
        background-size: 200% auto;
        animation: shimmer 3s linear infinite;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const isComplete = otp.every((digit) => digit !== "");

  return (
    <div className="otp-page-container">
      {/* Overlay */}
      <div className="otp-overlay" />

      {/* Main Container */}
      <div className="otp-main-container">
        {/* Left Side - Welcome Section */}
        <div className="otp-welcome-section">
          <div>
            <div className="otp-logo-container">
              <div className="otp-logo-box">
                <img
                  src={logo}
                  alt="SBA Systems Logo"
                  className="otp-logo-image"
                />
              </div>
              <span className="otp-logo-text">SBA Systems Analytics</span>
            </div>

            <div className="otp-welcome-content">
              <div className="otp-shield-container">
                <Shield size={32} color="#0ea5e9" />
                <h3 className="otp-shield-title">
                  Verify your <br />
                  <span className="shimmer-text">identity</span>
                </h3>
              </div>
              <p className="otp-welcome-text">
                We've sent a 6-digit verification code to <br />
                <strong style={{ color: "#0ea5e9" }}>{email}</strong>
              </p>
            </div>

            {/* Trust indicators */}
            <div className="otp-trust-indicators">
              <div className="otp-trust-item">
                <Lock size={16} color="#0ea5e9" />
                <span>256-bit encrypted</span>
              </div>
              <div className="otp-trust-item">
                <Clock size={16} color="#0ea5e9" />
                <span>Expires in {formatTime(timeLeft)}</span>
              </div>
            </div>

            {/* Progress indicator */}
            {isComplete && !success && (
              <div className="otp-progress-indicator">
                <CheckCircle size={18} color="#0ea5e9" />
                <span className="otp-progress-text">
                  Code complete — ready to verify
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="otp-footer">
            © {new Date().getFullYear()} SBA Systems. All rights reserved.
          </div>
        </div>

        {/* Right Side - OTP Form */}
        <div className="otp-form-section">
          {error && (
            <Alert variant="danger" className="otp-error-alert">
              {error}
            </Alert>
          )}

          {resendSuccess && (
            <Alert variant="success" className="otp-success-alert">
              <CheckCircle size={18} />
              <span>Verification code resent successfully!</span>
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="otp-success-alert">
              <CheckCircle size={18} />
              <span>Verification successful! Redirecting...</span>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* OTP Input Section */}
            <div className="otp-input-section">
              <div className="otp-input-header">
                <label className="otp-input-label">
                  Enter verification code
                </label>
                <button
                  type="button"
                  onClick={handleBack}
                  className="otp-back-button"
                  onMouseEnter={(e) => {
                    e.target.style.color = "#0ea5e9";
                    e.target.style.background = "#F0F9FF";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#8A9BB8";
                    e.target.style.background = "transparent";
                  }}
                >
                  <ArrowLeft size={14} />
                  Back
                </button>
              </div>

              <div
                className="otp-container"
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="otp-input"
                    style={{
                      width: "52px",
                      height: "64px",
                      textAlign: "center",
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#0ea5e9",
                      border: `2px solid ${digit ? "#0ea5e9" : "#D1DCEB"}`,
                      borderRadius: "14px",
                      background: digit ? "#F0F9FF" : "#FFFFFF",
                      outline: "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: digit
                        ? "0 4px 12px rgba(14, 165, 233, 0.1)"
                        : "none",
                      caretColor: "#0ea5e9",
                    }}
                    onMouseEnter={(e) => {
                      if (!digit && !isFocused) {
                        e.target.style.borderColor = "#8ABFE3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!digit && !isFocused) {
                        e.target.style.borderColor = "#D1DCEB";
                      }
                    }}
                  />
                ))}
              </div>

              {/* Resend section */}
              <div className="otp-resend-section">
                <div className="otp-timer">
                  <Clock size={14} />
                  <span>
                    {timeLeft > 0
                      ? `Code expires in ${formatTime(timeLeft)}`
                      : "Code expired"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={!canResend || loading}
                  className="otp-resend-button"
                  style={{
                    background: "none",
                    border: "none",
                    color: canResend ? "#0ea5e9" : "#D1DCEB",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: canResend ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    transition: "all 0.2s",
                    backgroundColor: canResend ? "#F0F9FF" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (canResend) {
                      e.target.style.backgroundColor = "#E0F2FE";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (canResend) {
                      e.target.style.backgroundColor = "#F0F9FF";
                    }
                  }}
                >
                  <RefreshCw
                    size={14}
                    style={{
                      animation:
                        loading && canResend
                          ? "spin 1s linear infinite"
                          : "none",
                    }}
                  />
                  {canResend ? "Resend code" : "Resend available soon"}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !isComplete || success}
              className="otp-submit-button"
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "15px",
                fontWeight: "500",
                backgroundColor: isComplete && !success ? "#0ea5e9" : "#D1DCEB",
                border: "none",
                borderRadius: "14px",
                cursor:
                  isComplete && !success && !loading
                    ? "pointer"
                    : "not-allowed",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                color: isComplete && !success ? "#FFFFFF" : "#8A9BB8",
              }}
              onMouseEnter={(e) => {
                if (isComplete && !success && !loading) {
                  e.target.style.backgroundColor = "#0284c7";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(14, 165, 233, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (isComplete && !success && !loading) {
                  e.target.style.backgroundColor = "#0ea5e9";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }
              }}
            >
              {loading ? (
                <>
                  <RefreshCw
                    size={18}
                    style={{ animation: "spin 0.8s linear infinite" }}
                  />
                  Verifying...
                </>
              ) : success ? (
                <>
                  <CheckCircle size={18} />
                  Verified!
                </>
              ) : (
                <>
                  <Fingerprint size={18} />
                  Verify & Sign In
                </>
              )}
            </Button>

            {/* Additional help text */}
            <div className="otp-help-text">
              <span className="otp-help-item">
                <Shield size={13} color="#8A9BB8" />
                Secured with 256-bit encryption
              </span>
              <span className="otp-help-divider">•</span>
              <span className="otp-help-item">
                <Mail size={13} color="#8A9BB8" />
                Check your spam folder
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
